import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Capacitor } from '@capacitor/core'
import {
  fetchDeviceContacts,
  requestContactsPermission,
  type ImportableContact,
} from '@/lib/contacts-import'

type Step = 'source' | 'loading' | 'preview' | 'importing' | 'done'

interface PreviewContact extends ImportableContact {
  selected: boolean
  isDuplicate: boolean
  existingContactId: string | null
}

export default function ImportContactsPage() {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('source')
  const [contacts, setContacts] = useState<PreviewContact[]>([])
  const [error, setError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{
    imported: number
    skipped: number
  } | null>(null)

  const isNative = Capacitor.isNativePlatform()

  async function handlePhoneImport() {
    setError(null)
    setStep('loading')

    try {
      const granted = await requestContactsPermission()
      if (!granted) {
        setError(t('import.permissionDenied', 'Contacts permission denied'))
        setStep('source')
        return
      }

      const deviceContacts = await fetchDeviceContacts()
      if (!deviceContacts.length) {
        setError(t('import.noContacts', 'No contacts found on device'))
        setStep('source')
        return
      }

      // Check for duplicates via API
      const previewRes = await fetch('/api/contacts/import/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contacts: deviceContacts.map((c) => ({
            first_name: c.firstName,
            last_name: c.lastName,
            email: c.email,
            phone: c.phone,
          })),
        }),
      })

      if (!previewRes.ok) {
        throw new Error('Failed to check duplicates')
      }

      const preview = await previewRes.json()

      setContacts(
        deviceContacts.map((c, i) => ({
          ...c,
          selected: !preview.results[i].isDuplicate,
          isDuplicate: preview.results[i].isDuplicate,
          existingContactId: preview.results[i].existingContactId,
        })),
      )
      setStep('preview')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
      setStep('source')
    }
  }

  function toggleContact(index: number) {
    setContacts((prev) => prev.map((c, i) => (i === index ? { ...c, selected: !c.selected } : c)))
  }

  function selectAll() {
    setContacts((prev) => prev.map((c) => ({ ...c, selected: true })))
  }

  function skipDuplicates() {
    setContacts((prev) => prev.map((c) => ({ ...c, selected: c.isDuplicate ? false : c.selected })))
  }

  async function handleImport() {
    const selected = contacts.filter((c) => c.selected)
    if (!selected.length) return

    setStep('importing')

    try {
      const res = await fetch('/api/contacts/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          contacts: selected.map((c) => ({
            first_name: c.firstName,
            last_name: c.lastName,
            email: c.email,
            phone: c.phone,
            company: c.company,
            job_title: c.jobTitle,
            birthday: c.birthday,
          })),
        }),
      })

      if (!res.ok) {
        throw new Error('Import failed')
      }

      const result = await res.json()
      setImportResult({
        imported: result.imported,
        skipped: contacts.length - selected.length,
      })
      setStep('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
      setStep('preview')
    }
  }

  return (
    <div className="flex flex-1 flex-col px-6 pt-6 pb-8">
      <h1 className="font-heading mb-4 text-2xl font-bold">
        {t('import.title', 'Import Contacts')}
      </h1>

      {error && (
        <div className="border-destructive/30 bg-destructive/10 text-destructive mb-4 rounded-lg border p-3 text-sm">
          {error}
        </div>
      )}

      {step === 'source' && (
        <div className="space-y-3">
          {isNative && (
            <button
              onClick={handlePhoneImport}
              className="bg-surface-container-low w-full rounded-xl p-4 text-left font-medium"
            >
              {t('import.fromPhone', 'Phone Contacts')}
            </button>
          )}
          <button
            disabled
            className="bg-surface-container-low w-full rounded-xl p-4 text-left font-medium opacity-50"
          >
            {t('import.fromGoogle', 'Google Contacts')} (coming soon)
          </button>
          <button
            disabled
            className="bg-surface-container-low w-full rounded-xl p-4 text-left font-medium opacity-50"
          >
            {t('import.fromCsv', 'CSV File')} (coming soon)
          </button>
        </div>
      )}

      {step === 'loading' && (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-on-surface-variant">{t('import.loading', 'Fetching contacts...')}</p>
        </div>
      )}

      {step === 'preview' && (
        <div className="flex flex-1 flex-col">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-on-surface-variant text-sm">
              {contacts.filter((c) => c.selected).length} / {contacts.length}{' '}
              {t('import.selected', 'selected')}
            </span>
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-primary text-sm font-medium">
                {t('import.selectAll', 'Select all')}
              </button>
              <button onClick={skipDuplicates} className="text-primary text-sm font-medium">
                {t('import.skipDuplicates', 'Skip duplicates')}
              </button>
            </div>
          </div>

          <div className="flex-1 space-y-1 overflow-y-auto">
            {contacts.map((contact, i) => (
              <label
                key={i}
                className="bg-surface-container-low flex items-center gap-3 rounded-xl p-3"
              >
                <input
                  type="checkbox"
                  checked={contact.selected}
                  onChange={() => toggleContact(i)}
                  className="h-4 w-4 rounded"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {contact.firstName} {contact.lastName}
                  </p>
                  {contact.email && (
                    <p className="text-on-surface-variant truncate text-sm">{contact.email}</p>
                  )}
                </div>
                {contact.isDuplicate && (
                  <span className="tag-coral shrink-0 rounded px-2 py-0.5 text-xs font-medium">
                    {t('import.duplicate', 'Exists')}
                  </span>
                )}
              </label>
            ))}
          </div>

          <button
            onClick={handleImport}
            disabled={!contacts.some((c) => c.selected)}
            className="bg-primary text-primary-foreground mt-4 w-full rounded-xl p-3 font-medium disabled:opacity-50"
          >
            {t('import.importSelected', 'Import Selected')} (
            {contacts.filter((c) => c.selected).length})
          </button>
        </div>
      )}

      {step === 'importing' && (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-on-surface-variant">
            {t('import.importing', 'Importing contacts...')}
          </p>
        </div>
      )}

      {step === 'done' && importResult && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <div className="text-center">
            <p className="text-4xl font-bold">{importResult.imported}</p>
            <p className="text-on-surface-variant">{t('import.imported', 'contacts imported')}</p>
          </div>
          {importResult.skipped > 0 && (
            <p className="text-on-surface-variant text-sm">
              {importResult.skipped} {t('import.skipped', 'skipped')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
